import { Column } from '@/shared/Column'
import {
  MacroItem,
  MacroScheduleItem,
  TemplateMacroVariableProps,
  VariableItem
} from '@/template/interface'
import { TemplateVariableTable } from '@/template/variable/table'
import { getFormatMessage } from '@/utils/utils'
import { LinkOutlined, ScheduleOutlined } from '@ant-design/icons'
import { Button, Form, Modal, Space, Table } from 'antd'
import React, { useEffect, useState } from 'react'
import { TemplateMacroPlan } from '../plan'
import styles from './index.css'

export const TemplateMacroVariable: React.FC<TemplateMacroVariableProps> = (
  props: TemplateMacroVariableProps
) => {
  const [variableVisible, setVariableVisible] = useState<boolean>(false)
  const [planForm] = Form.useForm<MacroScheduleItem>()
  const [planVisible, setPlanVisible] = useState<boolean>(false)
  const [macroVariables, setMacroVariables] = useState<VariableItem[]>([]) // 已选择的条目
  const [selectedVariables, setSelectedVarialbes] = useState<VariableItem[]>([]) // 选择列表中选中的条目
  const [filterVariables, setFilterVariables] = useState<VariableItem[]>([]) // 选择列表中剩余可选条目
  const formatMessage = getFormatMessage()
  const columns = [
    {
      title: formatMessage({ id: 'app.template.variable.table.name' }),
      key: 'app.template.variable.table.name',
      dataIndex: 'name'
    },
    {
      title: formatMessage({ id: 'app.template.variable.table.mainAddress' }),
      key: 'app.template.variable.table.mainAddress',
      render: (text: any, record: VariableItem, index: any) => {
        const plc = props.plcs?.find((x) => x.id == record.deviceId)
        const reg = plc?.registers?.find((x) => x.name == record.registerName)
        let addr = `${record.registerName} ${record.mainAddress}`
        if (reg?.hasSubAddress) {
          addr += `.${record.subAddress}`
        }
        if (reg?.hasSubIndex) {
          addr += `.${record.subIndex}`
        }
        return addr
      }
    },
    {
      title: formatMessage({ id: 'app.template.variable.table.operation' }),
      key: 'app.template.variable.table.operation',
      render: (text: any, record: VariableItem) => (
        <Space size='middle'>
          <a onClick={() => handleVariableRemove(record)}>
            {formatMessage({
              id: 'app.template.variable.table.operation.delete'
            })}
          </a>
        </Space>
      )
    }
  ]

  const selectColumn: Column[] = [
    { index: 0, hidden: true },
    { index: 4, hidden: true },
    { index: 5, hidden: true }
  ]

  const rowSelection = {
    getCheckboxProps: (record: VariableItem) => {
      return {
        disabled: record.enabledIndex
      }
    }
  }

  const handleOnVariableSelect = (items: VariableItem[]) => {
    setSelectedVarialbes(items)
  }

  const handleVariableModalOk = () => {
    const macro: MacroItem = { ...props.item }
    if (!macro.variableIds) {
      macro.variableIds = []
    }
    macro.variableIds = macro.variableIds.concat(
      selectedVariables.map((x) => x.id || '')
    )
    props.onItemChange?.(macro)
    setVariableVisible(false)
  }

  const handleVariableRemove = (record: VariableItem) => {
    const macro: MacroItem = { ...props.item }
    macro.variableIds = macro.variableIds?.filter((x) => x != record.id)
    props.onItemChange?.(macro)
  }

  const handlePlanModalOk = () => {
    const values = planForm.getFieldsValue()
    if (values.condition?.variableId) {
      const conVar = props.variables?.find(
        (x) => x.id === values.condition?.variableId
      )
      // 如果是位类型 变量
      if (conVar && conVar.dataType == 0) {
        values.condition.condition1 = 1 // 赋值 =
      }
    }
    const macro: MacroItem = { ...props.item }
    macro.schedules = [values]
    props.onItemChange?.(macro)
    setPlanVisible(false)
  }

  useEffect(() => {
    if (props.item) {
      const ids = props.item.variableIds || []
      setFilterVariables(
        props.variables?.filter((x) => !ids.includes(x.id!)) || []
      )
      setMacroVariables(
        props.variables?.filter((x) => ids.includes(x.id!)) || []
      )
    }
  }, [props.item])

  return (
    <div className={styles['variable']}>
      <div className={styles['variable-table']}>
        <Table
          rowKey='id'
          size='small'
          columns={columns}
          dataSource={macroVariables}
          pagination={false}
          scroll={{ y: 461 }}
        />
      </div>
      <div className={styles['variable-toolbar']}>
        <Space>
          <Button
            onClick={() => setVariableVisible(true)}
            type='primary'
            icon={<LinkOutlined />}
          >
            {formatMessage({ id: 'app.template.macro.variable.contact' })}
          </Button>
          <Button
            onClick={() => setPlanVisible(true)}
            type='primary'
            icon={<ScheduleOutlined />}
          >
            {formatMessage({ id: 'app.template.macro.plan' })}
          </Button>
        </Space>
      </div>

      <Modal
        visible={variableVisible}
        onCancel={() => setVariableVisible(false)}
        onOk={handleVariableModalOk}
        title={formatMessage({ id: 'app.template.macro.variable.title' })}
        width={500}
        okText={formatMessage({ id: 'app.modal.ok' })}
        cancelText={formatMessage({ id: 'app.modal.cancel' })}
        maskClosable={false}
        destroyOnClose={true}
      >
        <TemplateVariableTable
          size='small'
          selectable
          selectionType='checkbox'
          columns={selectColumn}
          plcs={props.plcs}
          data={filterVariables}
          rowSelection={rowSelection}
          onSelect={handleOnVariableSelect}
        />
      </Modal>

      <Modal
        visible={planVisible}
        onCancel={() => setPlanVisible(false)}
        onOk={handlePlanModalOk}
        title={formatMessage({ id: 'app.template.macro.plan.title' })}
        width={500}
        okText={formatMessage({ id: 'app.modal.ok' })}
        cancelText={formatMessage({ id: 'app.modal.cancel' })}
        maskClosable={false}
        destroyOnClose={true}
      >
        <div className={styles['plan']}>
          <TemplateMacroPlan
            form={planForm}
            plcs={props.plcs}
            variables={props.variables}
            item={props.item}
          />
        </div>
      </Modal>
    </div>
  )
}
